import { filter, first, includes, map, sortBy } from 'es-toolkit/compat';
import { enumValuesList } from 'utils/enumValues';

export interface StainType {
  id: string;
  displayName: string;
  index: number;
  canBeMifMarker: boolean;
  dna?: boolean | null;
  rna?: boolean | null;
  aliases?: string[];
  lineage?: boolean;
  phenotypic?: boolean;
  membrane?: boolean;
  nucleus?: boolean;
  cytoplasm?: boolean;
  function?: string;
  extracellular?: boolean;
  distal?: boolean;
  deletedAt?: string | null;
}

export const MULTIPLEX_STAIN_ID = 'mplex' as const;
export const HEMATOXYLIN_STAIN_ID = 'hematoxylin' as const;
export const HEMATOXYLIN_STAIN_DISPLAY_NAME = 'Hematoxylin' as const;

export enum StainingMethod {
  he = 'he',
  mplex = 'mplex',
  chromogenicMplex = 'chromogenic_mplex',
  ihc = 'ihc',
}

export const stainingMethodsList = enumValuesList(StainingMethod);

export const getIsMultiStain = (slideStainType: string): boolean => {
  const stainingMethod = getStainingMethodByStainType(slideStainType);
  return stainingMethod === StainingMethod.mplex || stainingMethod === StainingMethod.chromogenicMplex;
};

export const getStainingMethodByStainType = (slideStainType?: string): StainingMethod => {
  // If slideStainType is 'he' or 'mplex', we return it as is
  if (includes(['he', MULTIPLEX_STAIN_ID], slideStainType)) return slideStainType as StainingMethod;
  // In case we have more than 1 ihc, separated by '_', we consider it as chromogenicMplex
  if (includes(slideStainType, '_')) return StainingMethod.chromogenicMplex;
  // Otherwise, we return ihc
  return StainingMethod.ihc;
  // In the future, we may want to deal with non dab ihc differently
};

interface MultiplexDnaStainTypeOption {
  text: string;
  value: string;
}

interface MultiplexDnaStainTypeData {
  dnaStainTypes: StainType[];
  dnaStainTypeIds: string[];
  defaultStainTypeId?: string;
  options: MultiplexDnaStainTypeOption[];
}

export const getMultiplexDnaStainTypeData = (stainTypes: StainType[]): MultiplexDnaStainTypeData => {
  const dnaStainTypes = sortBy(
    filter(stainTypes, (stainType) => Boolean(stainType.dna) && !stainType.deletedAt),
    ['index', 'id']
  );

  const dnaStainTypeIds = map(dnaStainTypes, (stainType) => stainType.id);

  return {
    dnaStainTypes,
    dnaStainTypeIds,
    defaultStainTypeId: first(dnaStainTypeIds),
    options: map(dnaStainTypes, (stainType) => ({
      text: stainType.displayName || stainType.id,
      value: stainType.id,
    })),
  };
};
